import React from 'react';
import styles from '../../pages/DiagramPage/DiagramEditor.module.css';
import { Shape, Connection, ShapeStyle, TextElement, FreeLine } from '../../types/diagram.types';
import { DEFAULT_SHAPE_PRESET, SHAPE_PRESET_ORDER, SHAPE_STYLE_PRESETS } from '../../config/shapePresets';
import { useLanguage } from '../../context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faTrash } from '@fortawesome/free-solid-svg-icons';

interface PropertiesPanelProps {
  selectedShape?: Shape;
  selectedConnection?: Connection;
  selectedText?: TextElement;
  selectedLine?: FreeLine;
  isEditMode: boolean;
  onUpdateShape: (shapeId: string, updates: Partial<Shape>) => void;
  onUpdateConnection: (connId: string, updates: Partial<Connection>) => void;
  onUpdateText: (textId: string, updates: Partial<TextElement>) => void;
  onUpdateLine: (lineId: string, updates: Partial<FreeLine>) => void;
  onDeleteShape: (shapeId: string) => void;
  onDeleteConnection: (connId: string) => void;
  onDeleteText: (textId: string) => void;
  onDeleteLine: (lineId: string) => void;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedShape,
  selectedConnection,
  selectedText,
  selectedLine,
  isEditMode,
  onUpdateShape,
  onUpdateConnection,
  onUpdateText,
  onUpdateLine,
  onDeleteShape,
  onDeleteConnection,
  onDeleteText,
  onDeleteLine,
}) => {
  const { t } = useLanguage();
  
  if (!selectedShape && !selectedConnection && !selectedText && !selectedLine) return null;

  if (selectedLine) {
    return (
      <div className={styles.propertiesPanel}>
        <div className={styles.propertiesHeader}>
          <FontAwesomeIcon icon={faGear} /> {t('lineProperties')}
        </div>

        <div className={styles.propertiesContent}>
          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('lineColor')}:</label>
            <input
              type="color"
              className={styles.colorInput}
              value={selectedLine.color}
              onChange={e =>
                onUpdateLine(selectedLine.id, { color: e.target.value })
              }
              disabled={!isEditMode}
            />
          </div>

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('lineWidth')}:</label>
            <input
              type="number"
              className={styles.propertyInput}
              value={selectedLine.width}
              min={1}
              max={20}
              onChange={e =>
                onUpdateLine(selectedLine.id, {
                  width: parseInt(e.target.value),
                })
              }
              disabled={!isEditMode}
            />
          </div>

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('pointsCount')}:</label>
            <div className={styles.propertyValue}>
              {selectedLine.points.length}
            </div>
          </div>

          <button
            className={styles.deleteButton}
            onClick={() => onDeleteLine(selectedLine.id)}
            disabled={!isEditMode}
          >
            <FontAwesomeIcon icon={faTrash} /> {t('deleteLine')}
          </button>
        </div>
      </div>
    );
  }

  if (selectedText) {
    return (
      <div className={styles.propertiesPanel}>
        <div className={styles.propertiesHeader}><FontAwesomeIcon icon={faGear} />
        {t('textProperties')}
        </div>

        <div className={styles.propertiesContent}>
          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('text')}:</label>
            <textarea
              className={styles.propertyInput}
              value={selectedText.text}
              onChange={e =>
                onUpdateText(selectedText.id, { text: e.target.value })
              }
              rows={3}
              style={{ resize: 'vertical' }}
              disabled={!isEditMode}
            />
          </div>

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('fontSize')}:</label>
            <input
              type="number"
              className={styles.propertyInput}
              value={selectedText.fontSize}
              min={8}
              max={72}
              onChange={e =>
                onUpdateText(selectedText.id, {
                  fontSize: parseInt(e.target.value),
                })
              }
              disabled={!isEditMode}
            />
          </div>

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('textColor')}:</label>
            <input
              type="color"
              className={styles.colorInput}
              value={selectedText.color}
              onChange={e =>
                onUpdateText(selectedText.id, { color: e.target.value })
              }
              disabled={!isEditMode}
            />
          </div>

          <button
            className={styles.deleteButton}
            onClick={() => onDeleteText(selectedText.id)}
            disabled={!isEditMode}
          >
            <FontAwesomeIcon icon={faTrash} /> {t('deleteText')}
          </button>
        </div>
      </div>
    );
  }

  if (selectedShape) {
    const presetKey = selectedShape.stylePreset ?? DEFAULT_SHAPE_PRESET;
    const presetStyle =
      SHAPE_STYLE_PRESETS[presetKey] ??
      SHAPE_STYLE_PRESETS[DEFAULT_SHAPE_PRESET];

    const style: ShapeStyle = {
      ...presetStyle,
      ...(selectedShape.style ?? {}),
    };

    const updateStyle = (patch: Partial<ShapeStyle>) => {
      onUpdateShape(selectedShape.id, {
        style: {
          ...(selectedShape.style ?? {}),
          ...patch,
        },
      });
    }

    return (
      <div className={styles.propertiesPanel}>
        <div className={styles.propertiesHeader}><FontAwesomeIcon icon={faGear} />
        {t('shapeProperties')}
        </div>

        <div className={styles.propertiesContent}>
          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('style')}:</label>
            <div className={styles.stylePresetRow}>
              {SHAPE_PRESET_ORDER.map(preset => (
                <button
                  key={preset}
                  className={`${styles.presetButton} ${
                    presetKey === preset ? styles.presetActive : ''
                  }`}
                  onClick={() =>
                    onUpdateShape(selectedShape.id, {
                      stylePreset: preset,
                      style: undefined, 
                    })
                  }
                  disabled={!isEditMode}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('text')}:</label>
            <input
              className={styles.propertyInput}
              value={selectedShape.text}
              onChange={e =>
                onUpdateShape(selectedShape.id, { text: e.target.value })
              }
              disabled={!isEditMode}
            />
          </div>

          <button
            className={styles.textButton}
            onClick={() => onUpdateShape(selectedShape.id, { text: '' })}
            disabled={!isEditMode}
          >
            {t('clearText')}
          </button>

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('fontSize')}:</label>
            <input
              type="number"
              className={styles.propertyInput}
              value={selectedShape.fontSize}
              min={8}
              max={48}
              onChange={e =>
                onUpdateShape(selectedShape.id, {
                  fontSize: parseInt(e.target.value),
                })
              }
              disabled={!isEditMode}
            />
          </div>

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('fillType')}:</label>
            <select
              className={styles.propertyInput}
              value={style.fillStyle}
              onChange={e =>
                updateStyle({ fillStyle: e.target.value as any })
              }
              disabled={!isEditMode}
            >
              <option value="solid">{t('solid')}</option>
              <option value="gradient">{t('gradient')}</option>
            </select>
          </div>

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('fillColor')}:</label>
            <input
              type="color"
              className={styles.colorInput}
              value={style.fillColor}
              onChange={e => updateStyle({ fillColor: e.target.value })}
              disabled={!isEditMode}
            />
          </div>

          {style.fillStyle === 'gradient' && (
            <>
              <div className={styles.propertyGroup}>
                <label className={styles.propertyLabel}>{t('gradientTop')}:</label>
                <input
                  type="color"
                  className={styles.colorInput}
                  value={style.gradientFrom}
                  onChange={e =>
                    updateStyle({ gradientFrom: e.target.value })
                  }
                  disabled={!isEditMode}
                />
              </div>

              <div className={styles.propertyGroup}>
                <label className={styles.propertyLabel}>{t('gradientBottom')}:</label>
                <input
                  type="color"
                  className={styles.colorInput}
                  value={style.gradientTo}
                  onChange={e =>
                    updateStyle({ gradientTo: e.target.value })
                  }
                  disabled={!isEditMode}
                />
              </div>
            </>
          )}

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('borderColor')}:</label>
            <input
              type="color"
              className={styles.colorInput}
              value={style.borderColor}
              onChange={e =>
                updateStyle({ borderColor: e.target.value })
              }
              disabled={!isEditMode}
            />
          </div>

          <div className={styles.propertyGroup}>
            <label className={styles.propertyLabel}>{t('borderWidth')}:</label>
            <input
              type="number"
              className={styles.propertyInput}
              value={style.borderWidth}
              min={1}
              max={10}
              onChange={e =>
                updateStyle({ borderWidth: parseInt(e.target.value) })
              }
              disabled={!isEditMode}
            />
          </div>

          <div className={styles.propertyGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.checkboxInput}
                checked={style.shadow}
                onChange={e => updateStyle({ shadow: e.target.checked })}
                disabled={!isEditMode}
              />
              <span className={styles.checkboxCustom}></span>
              <span className={styles.checkboxText}>{t('shadow')}</span>
            </label>
          </div>

          <button
            className={styles.deleteButton}
            onClick={() => onDeleteShape(selectedShape.id)}
            disabled={!isEditMode}
          >
            <FontAwesomeIcon icon={faTrash} /> {t('deleteShape')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.propertiesPanel}>
      <div className={styles.propertiesHeader}><FontAwesomeIcon icon={faGear} />
      {t('connectionProperties')}
      </div>

      <div className={styles.propertiesContent}>
        <div className={styles.propertyGroup}>
          <label className={styles.propertyLabel}>{t('connectionType')}:</label>
          <select
            className={styles.propertyInput}
            value={selectedConnection!.type}
            onChange={e =>
              onUpdateConnection(selectedConnection!.id, {
                type: e.target.value as any,
              })
            }
            disabled={!isEditMode}
          >
            <option value="arrow">{t('arrow')}</option>
            <option value="line">{t('line')}</option>
            <option value="dashed">{t('dashed')}</option>
          </select>
        </div>

        <div className={styles.propertyGroup}>
          <label className={styles.propertyLabel}>{t('lineColor')}:</label>
          <input
            type="color"
            className={styles.colorInput}
            value={selectedConnection!.color}
            onChange={e =>
              onUpdateConnection(selectedConnection!.id, {
                color: e.target.value,
              })
            }
            disabled={!isEditMode}
          />
        </div>

        <div className={styles.propertyGroup}>
          <label className={styles.propertyLabel}>{t('lineWidth')}:</label>
          <input
            type="number"
            className={styles.propertyInput}
            value={selectedConnection!.width}
            min={1}
            max={10}
            onChange={e =>
              onUpdateConnection(selectedConnection!.id, {
                width: parseInt(e.target.value),
              })
            }
            disabled={!isEditMode}
          />
        </div>

        <button
          className={styles.deleteButton}
          onClick={() => onDeleteConnection(selectedConnection!.id)}
          disabled={!isEditMode}
        >
          <FontAwesomeIcon icon={faTrash} /> {t('deleteConnection')}
        </button>
      </div>
    </div>
  );
};